import React from 'react';
import { useI18n } from '../../i18n';
import type { StaffRoleRef } from '../../services/roleService';
import type { StaffRole } from '../../services/staffService';
import { roleDisplayName, staffRoleLabel } from './staffPresentation';

interface Props {
  roles: StaffRoleRef[];
  /** Shown when the roles themselves are not known, so a person is never unlabelled. */
  tier: StaffRole;
  isOwner?: boolean;
}

/** A role's own colour, when the company gave it one. */
const chipStyle = (role: StaffRoleRef): React.CSSProperties | undefined => (
  role.colour ? { borderColor: role.colour, color: role.colour } : undefined
);

/** Every role somebody holds, most senior first, with the owner marked ahead of them. */
export default function RoleChips({ roles, tier, isOwner = false }: Props) {
  const { t } = useI18n();
  // Management before guard: it is the larger claim about what they can do.
  const ordered = [
    ...roles.filter((role) => role.kind === 'management'),
    ...roles.filter((role) => role.kind === 'guard'),
  ];

  return (
    <span className="staff-roles">
      {isOwner && <span className="staff-role staff-role--owner">{t('System Owner')}</span>}
      {ordered.length > 0 ? ordered.map((role) => (
        <span
          key={role.id}
          className={`staff-role staff-role--${role.systemKey === 'system_admin' ? 'admin' : role.kind === 'management' ? 'manager' : 'guard'}`}
          style={chipStyle(role)}
        >
          {roleDisplayName(role)}
        </span>
      )) : (
        <span className={`staff-role staff-role--${tier}`}>{staffRoleLabel(tier)}</span>
      )}
    </span>
  );
}
